'use client';
import useLocale from '@/hooks/useLocale';
import { getPlatform } from '@/utils/helpers';
import {
  LDProvider,
  useFlags,
  useLDClient,
} from 'launchdarkly-react-client-sdk';
import { useEffect } from 'react';
import useSubscriptions, { UserSubscriptions } from './SubscriptionContext';
import useAuth, { AuthState } from './AuthContext';
import { sessionFlagsRetrieved } from '@/utils/sessionFlags';
import { getFallbackLeiaId, getLeiaDeviceId } from '@/utils/leiaClientSide';

export function deviceContext(leia_device_id?: string | null) {
  if (!leia_device_id) return;

  return {
    device: {
      key: leia_device_id,
      kind: 'device',
    },
  };
}

export function userContext(
  user: AuthState | null,
  subscriptions: UserSubscriptions | undefined,
) {
  const userUuid = user?.attributes['custom:account_manager_id'];
  if (!userUuid) return;

  const userContext: {
    key: string;
    kind: string;
    email?: string;
    subscriptions?: UserSubscriptions['summary'];
    _meta?: {
      privateAttributes: string[];
    };
  } = {
    key: userUuid,
    kind: 'user',
  };
  if (user.attributes.email) {
    userContext.email = user.attributes.email;
    userContext._meta = {
      privateAttributes: ['email'],
    };
  }
  if (subscriptions && subscriptions.summary) {
    userContext.subscriptions = subscriptions.summary;
  }

  return {
    user: userContext,
  };
}

export function buildLDContext({
  region,
  locale,
  platform,
  subscriptions,
  user,
  leia_device_id,
}: {
  region: string;
  locale: string;
  platform: string;
  subscriptions?: UserSubscriptions;
  user: AuthState | null;
  leia_device_id?: string | null;
}) {
  const baseContext = {
    kind: 'multi',
    region: {
      key: region,
      kind: 'region',
    },
    locale: {
      key: locale,
      kind: 'locale',
    },
    platform: {
      key: platform,
      kind: 'platform',
    },
  };

  return {
    ...baseContext,
    ...deviceContext(leia_device_id),
    ...userContext(user, subscriptions),
  };
}

const FlagAnalytics = () => {
  const flags = useFlags();
  const client = useLDClient();

  useEffect(() => {
    if (client === undefined || typeof client.getContext !== 'function') return;

    // Initial sync
    sessionFlagsRetrieved(flags, client.getContext());

    // Listen for flag changes
    const handleFlagChange = () => {
      // Get fresh flags after change
      sessionFlagsRetrieved(client?.allFlags(), client.getContext());
    };

    client?.on('change', handleFlagChange);

    // Cleanup
    return () => {
      client?.off('change', handleFlagChange);
      // Clear flags on unmount?
    };
  }, [client, flags]);

  // No need to render anything
  return null;
};

export const FeatureProvider = ({ children }: { children: any }) => {
  const region = process.env.NEXT_PUBLIC_REGION || '';
  const { data: subscriptions } = useSubscriptions();
  const { user } = useAuth();
  const leia_device_id = getLeiaDeviceId() || getFallbackLeiaId();
  const locale = useLocale();
  const platform = getPlatform();

  const context = buildLDContext({
    region,
    locale,
    platform,
    subscriptions,
    user,
    leia_device_id,
  });

  return (
    <LDProvider
      clientSideID={process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_KEY || ''}
      context={context}
      deferInitialization={true}
      reactOptions={{ useCamelCaseFlagKeys: false }}
    >
      <FlagAnalytics />
      {children}
    </LDProvider>
  );
};
